/**
 * VoicemailData.js
 * Talkroute
 *
 * Created by Vel Genov on 2/12/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */

import moment, { Moment } from "moment";

import { CallData, formatPhoneNumber } from "./CallData";
import uiString from "../../constants/uiString";
import { IContact } from "@models/Contacts.models";

export const WELCOME_VOICEMAIL = 1;

/**
 * A voicemail data class
 *
 * @param messageRead a boolean to indicate if a message was read
 * @param mailboxId the ID of the mailbox this message is in
 */
export class VoicemailData extends CallData {
  public messageRead: boolean;
  public mailboxId: string;
  public contact?: IContact;
  constructor(opts) {
    super(opts);

    this.messageRead = opts.messageRead;
    this.mailboxId = opts.mailboxId.toString();
    this.contact = opts.contact;
  }

  /**
   * Getter fot the callerNumber
   *
   * @returns the caller phone number
   */
  getCallerPhoneNumber = () => this.callerNumber;

  /**
   * Return the caller contact name if available.
   * If not available, use cname, and if cname is not available - use phone number;
   * 
   * @returns contact name or cname or phone number
   */
  getContactInfo = () => {
   if (this.contact) return this.contact.name;
   return this.getCallerNameOrNumber();
  }

  /**
   * Sets the contact name to equal the first parameter.
   * @param name
   * @returns A new instance of the class with the updated contact name
   */
  setContactInfo = (contact: IContact) => new VoicemailData({
   date: this.date,
   callerCName: this.callerCName,
   duration: this.duration,
   id: this.id,
   callerNumber: this.callerNumber,
   messageRead: this.messageRead,
   mailboxId: this.mailboxId,
   contact: contact
  });


  getCallerNumberFormatted = () => formatPhoneNumber(this.callerNumber);

  getCallerNameOrNumber = () => {
    if (this.callerCName) return this.callerCName;

    return formatPhoneNumber(this.callerNumber);
  };

  getMessageReadString = () =>
    this.messageRead ? uiString.READ : uiString.UNREAD;
}

export const createWelcomeVoicemail = (isMessageRead: boolean = false, customDate?: moment.Moment) => {
  const nowDate = customDate ?? moment();
  const welcomeVoicemail = new VoicemailData({
    messageRead: isMessageRead,
    callerCName: uiString.welcomeVoicemail.FROM,
    callerNumber: uiString.welcomeVoicemail.FROM,
    mailboxId: uiString.welcomeVoicemail.MAILBOX,
    id: WELCOME_VOICEMAIL,
    date: nowDate,
    duration: uiString.welcomeVoicemail.DURATION,
  });

  return welcomeVoicemail;
};

/**
 * Parses the incoming JSON voicemails data. Creates a VoicemailData object for each line item
 *
 * @param {*} voicemailsData an array containig voicemails data.
 */
export const voicemailsJSONParser = (voicemailsData) => {
  // If valid data is passed, proceed. Otherwise don't return anything
  if (Array.isArray(voicemailsData) && voicemailsData.length > 0) {
    // Process each entry
    const parsedData = voicemailsData.map((entry) => {
      const {
        message_read: messageRead,
        caller_cname: callerCName,
        caller_number: callerNumber,
        vm_box_id: mailboxId,
        id,
        created: date,
        duration,
      } = entry;

      const voicemailEntry = new VoicemailData({
        messageRead,
        callerCName,
        callerNumber,
        mailboxId,
        id,
        date,
        duration,
      });

      return voicemailEntry;
    });

    return parsedData;
  } else {
    return []
  }
};

/**
 * Parses the incoming generic objects into VoicemailData objects.
 * Checks for empty objects passed in
 *
 * @param {*} voicemailsData an array containig voicemails.
 */
export const voicemailsRehydrator = (voicemailsData) => {
  // If valid data is passed, proceed. Otherwise don't return anything
  if (Array.isArray(voicemailsData) && voicemailsData.length > 0) {
    // Process each entry
    const parsedData = voicemailsData.map((entry) =>
      voicemailsRehydrateVoicemail(entry)
    );

    return parsedData;
  }
  return [];
};

/**
 * Parses the incoming single generic object into VoicemailData.
 * Checks for an empty object being passed in.
 *
 * @param {*} voicemail a single generic object containing a voicemail.
 */
export const voicemailsRehydrateVoicemail = (voicemail) => {
  if (voicemail) {
    const {
      messageRead,
      callerCName,
      callerNumber,
      mailboxId,
      id,
      date,
      duration,
    } = voicemail;

    const voicemailEntry = new VoicemailData({
      messageRead,
      callerCName,
      callerNumber,
      mailboxId,
      id,
      date,
      duration,
    });

    return voicemailEntry;
  }
  return voicemail;
};
